import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, KeyRound, QrCode, AlertTriangle } from 'lucide-react';
import IntestazioneSezione from '../components/comuni/IntestazioneSezione';
import MiaIscrizione from '../components/medici/MiaIscrizione';
import { useAuthStore } from '../store/useStore';
import { secondoFattoreAPI } from '../services/api';
import '../styles/Profilo.css';

/**
 * Profilo — quello che un utente può fare sul proprio account.
 *
 * Oggi c'è una cosa sola, la verifica in due passaggi. È una schermata a
 * sé e non un riquadro dentro «Cambia password» perché sono due cose che
 * si fanno in momenti diversi: la password si cambia quando qualcuno te
 * lo impone, il secondo fattore si attiva una volta e poi si torna qui
 * solo per rigenerare i codici o per disattivarlo.
 *
 * I CODICI DI RECUPERO SI VEDONO UNA VOLTA SOLA. Il server li restituisce
 * in chiaro nella risposta e poi conserva solo l'impronta: se chi è
 * davanti allo schermo chiude la pagina senza copiarli, nessuno può più
 * mostrarglieli — si possono solo rigenerare. L'avviso a schermo dice
 * esattamente questo, e non è un tono allarmista di troppo.
 */

const Profilo = () => {
    const user = useAuthStore((state) => state.user);
    const navigate = useNavigate();

    const [stato, setStato] = useState(null);
    const [caricando, setCaricando] = useState(true);
    const [errore, setErrore] = useState('');
    const [esito, setEsito] = useState('');

    // Attivazione in corso
    const [attivazione, setAttivazione] = useState(null);   // { qr, segreto }
    const [codice, setCodice] = useState('');
    const [codiciRecupero, setCodiciRecupero] = useState(null);

    // Finestra di conferma per disattivare o rigenerare
    const [conferma, setConferma] = useState(null);         // 'disattiva' | 'codici'
    const [password, setPassword] = useState('');
    const [codiceConferma, setCodiceConferma] = useState('');
    const [inCorso, setInCorso] = useState(false);

    const carica = async () => {
        setCaricando(true);
        try {
            const r = await secondoFattoreAPI.stato();
            setStato(r.data.data);
            setErrore('');
        } catch (e) {
            setErrore(e.message || 'Non riesco a leggere lo stato.');
        } finally {
            setCaricando(false);
        }
    };

    useEffect(() => { carica(); }, []);

    const avvia = async () => {
        setErrore(''); setEsito(''); setInCorso(true);
        try {
            const r = await secondoFattoreAPI.avvia();
            setAttivazione(r.data.data);
            setCodice('');
        } catch (e) {
            setErrore(e.message || 'Non riesco ad avviare l’attivazione.');
        } finally {
            setInCorso(false);
        }
    };

    const conferma6 = async (e) => {
        e.preventDefault();
        setErrore(''); setInCorso(true);
        try {
            const r = await secondoFattoreAPI.conferma(codice);
            setCodiciRecupero(r.data.data.codici_recupero);
            setAttivazione(null);
            setCodice('');
            setEsito(r.data.message);
            await carica();
        } catch (e2) {
            setErrore(e2.message || 'Codice non valido.');
            setCodice('');
        } finally {
            setInCorso(false);
        }
    };

    const eseguiConferma = async (e) => {
        e.preventDefault();
        setErrore(''); setInCorso(true);
        try {
            if (conferma === 'disattiva') {
                const r = await secondoFattoreAPI.disattiva(password, codiceConferma);
                setEsito(r.data.message);
                setCodiciRecupero(null);
            } else {
                const r = await secondoFattoreAPI.rigeneraCodici(password, codiceConferma);
                setCodiciRecupero(r.data.data.codici_recupero);
                setEsito(r.data.message);
            }
            setConferma(null); setPassword(''); setCodiceConferma('');
            await carica();
        } catch (e2) {
            setErrore(e2.message || 'Password o codice non validi.');
        } finally {
            setInCorso(false);
        }
    };

    const attivo = stato?.stato === 'attivo';

    return (
        <div className="prof-pagina">
            <IntestazioneSezione
                etichetta="Il mio account"
                titolo={`${user?.nome || ''} ${user?.cognome || ''}`.trim() || 'Profilo'}
                icona={<ShieldCheck size={22} aria-hidden="true" />}
                sottotitolo={<>
                    Username <b>{user?.username}</b> · ruolo <b>{user?.ruolo}</b>
                </>}
                azione={
                    <button className="prof-btn-chiaro" onClick={() => navigate('/cambia-password')}>
                        <KeyRound size={16} aria-hidden="true" /> Cambia password
                    </button>
                }
            />

            {errore && <p className="prof-errore">{errore}</p>}
            {esito && <p className="prof-esito">{esito}</p>}

            {/* L'avviso di chi è obbligato e non l'ha ancora attivato. */}
            {stato?.da_attivare && (
                <div className={stato.bloccante ? 'prof-avviso prof-avviso-forte' : 'prof-avviso'}>
                    <AlertTriangle size={18} aria-hidden="true" />
                    <div>
                        {stato.bloccante ? (
                            <>
                                <b>Il tempo per attivarla è finito.</b> Fino a quando non
                                attivi la verifica in due passaggi puoi restare solo su
                                questa schermata: le altre non rispondono.
                            </>
                        ) : (
                            <>
                                <b>Per il tuo ruolo la verifica in due passaggi è
                                obbligatoria.</b> C’è tempo fino al {stato.grazia_fino}.
                                Dopo quella data si entra solo per attivarla.
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Solo chi e' collegato a un medico: per gli altri il riquadro
                non c'e', e il server rifiuta comunque. */}
            {user?.medico_id && <MiaIscrizione />}

            <section className="prof-riquadro">
                <h2 className="prof-titolo">Verifica in due passaggi</h2>
                <p className="prof-spiega">
                    Con la verifica attiva, per entrare servono la password <i>e</i> un
                    codice di sei cifre generato dal tuo telefono. Chi dovesse arrivare
                    alla tua password — indovinata, riusata da un altro sito, finita in
                    una fuga di dati — non entra lo stesso.
                </p>

                {caricando ? (
                    <p className="prof-attesa">Carico…</p>
                ) : (
                    <>
                        <p className="prof-stato">
                            Stato:{' '}
                            <span className={attivo ? 'prof-pallino-si' : 'prof-pallino-no'}>
                                {attivo ? 'attiva' : 'non attiva'}
                            </span>
                            {attivo && stato.attivato_il && (
                                <> · dal {new Date(stato.attivato_il).toLocaleDateString('it-IT')}</>
                            )}
                            {attivo && stato.codici_rimasti !== null && (
                                <> · codici di recupero non ancora usati: <b>{stato.codici_rimasti}</b></>
                            )}
                        </p>

                        {/* --- non attiva, nessuna attivazione in corso --- */}
                        {!attivo && !attivazione && (
                            <button className="prof-btn" onClick={avvia} disabled={inCorso}>
                                <QrCode size={16} aria-hidden="true" /> Attiva la verifica in due passaggi
                            </button>
                        )}

                        {/* --- attivazione in corso --- */}
                        {attivazione && (
                            <div className="prof-attivazione">
                                <ol className="prof-passi">
                                    <li>
                                        Installa sul telefono un’app di autenticazione
                                        (Google Authenticator, Microsoft Authenticator, Aegis,
                                        o quella che già usi).
                                    </li>
                                    <li>Inquadra questo codice:</li>
                                </ol>

                                <img className="prof-qr" src={attivazione.qr}
                                     alt="Codice da inquadrare con l’app di autenticazione"
                                     width="220" height="220" />

                                <p className="prof-manuale">
                                    Se la fotocamera non lo legge, nell’app scegli
                                    «inserimento manuale» e digita questa chiave:
                                </p>
                                <p className="prof-segreto">{attivazione.segreto}</p>
                                <p className="prof-manuale">
                                    La chiave si vede adesso e basta. Da qui in poi non la
                                    mostra più nessuno: se serve di nuovo, si rifà
                                    l’attivazione da capo.
                                </p>

                                <form onSubmit={conferma6} className="prof-form-codice">
                                    <label htmlFor="codice-attivazione">
                                        Le sei cifre che l’app mostra adesso
                                    </label>
                                    <input
                                        id="codice-attivazione"
                                        className="prof-campo-cifre"
                                        value={codice}
                                        onChange={(e) => setCodice(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                        inputMode="numeric"
                                        placeholder="000000"
                                        maxLength={6}
                                        autoComplete="one-time-code"
                                        disabled={inCorso}
                                    />
                                    <div className="prof-azioni">
                                        <button type="submit" className="prof-btn"
                                                disabled={inCorso || codice.length !== 6}>
                                            Conferma e attiva
                                        </button>
                                        <button type="button" className="prof-btn-chiaro"
                                                onClick={() => { setAttivazione(null); setCodice(''); }}
                                                disabled={inCorso}>
                                            Annulla
                                        </button>
                                    </div>
                                </form>
                            </div>
                        )}

                        {/* --- attiva --- */}
                        {attivo && !attivazione && (
                            <div className="prof-azioni">
                                <button className="prof-btn-chiaro"
                                        onClick={() => { setConferma('codici'); setErrore(''); setEsito(''); }}>
                                    Rigenera i codici di recupero
                                </button>
                                <button className="prof-btn-rosso"
                                        onClick={() => { setConferma('disattiva'); setErrore(''); setEsito(''); }}>
                                    Disattiva
                                </button>
                            </div>
                        )}
                    </>
                )}
            </section>

            {/* --- gli otto codici, una volta sola --- */}
            {codiciRecupero && (
                <section className="prof-riquadro prof-riquadro-codici">
                    <h2 className="prof-titolo">I tuoi codici di recupero</h2>
                    <p className="prof-avviso prof-avviso-forte">
                        <AlertTriangle size={18} aria-hidden="true" />
                        <span>
                            <b>Questa schermata non torna.</b> Copiali adesso e tienili
                            dove tieni le cose importanti — non nel telefono che genera i
                            codici, altrimenti perdendo quello perdi entrambi. Servono a
                            entrare quando il telefono non ce l’hai: ognuno vale una
                            volta sola.
                        </span>
                    </p>
                    <ul className="prof-codici">
                        {codiciRecupero.map((c) => <li key={c}>{c}</li>)}
                    </ul>
                    <button className="prof-btn-chiaro" onClick={() => setCodiciRecupero(null)}>
                        Li ho copiati, nascondili
                    </button>
                </section>
            )}

            {/* --- password + codice, per disattivare o rigenerare --- */}
            {conferma && (
                <div className="prof-velo" role="dialog" aria-modal="true">
                    <form className="prof-finestra" onSubmit={eseguiConferma}>
                        <h3 className="prof-titolo">
                            {conferma === 'disattiva'
                                ? 'Disattivare la verifica in due passaggi?'
                                : 'Rigenerare i codici di recupero?'}
                        </h3>
                        <p className="prof-spiega">
                            {conferma === 'disattiva'
                                ? 'Da quel momento per entrare basterà la password. Se il tuo ruolo la richiede, al prossimo accesso ti verrà chiesto di riattivarla.'
                                : 'Gli otto codici di adesso smettono di funzionare e ne ricevi otto nuovi, da copiare subito.'}
                        </p>
                        <p className="prof-spiega">
                            Servono la password <b>e</b> un codice valido: è l’unico modo
                            perché chi trovasse il computer aperto non possa spegnerla.
                        </p>

                        <label htmlFor="prof-pw">Password</label>
                        <input id="prof-pw" type="password" value={password}
                               onChange={(e) => setPassword(e.target.value)}
                               autoComplete="current-password" disabled={inCorso} required />

                        <label htmlFor="prof-cod">Codice dall’app</label>
                        <input id="prof-cod" className="prof-campo-cifre" value={codiceConferma}
                               onChange={(e) => setCodiceConferma(e.target.value.replace(/\D/g, '').slice(0, 6))}
                               inputMode="numeric" placeholder="000000" maxLength={6}
                               autoComplete="one-time-code" disabled={inCorso} required />

                        {errore && <p className="prof-errore">{errore}</p>}

                        <div className="prof-azioni">
                            <button type="submit"
                                    className={conferma === 'disattiva' ? 'prof-btn-rosso' : 'prof-btn'}
                                    disabled={inCorso}>
                                {conferma === 'disattiva' ? 'Disattiva' : 'Rigenera'}
                            </button>
                            <button type="button" className="prof-btn-chiaro"
                                    onClick={() => { setConferma(null); setPassword(''); setCodiceConferma(''); setErrore(''); }}
                                    disabled={inCorso}>
                                Annulla
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};

export default Profilo;
